import { backbone, LocationHooks } from '@visikon/backbone';
import { Translation } from '@visikon/core-models/languageTypes';
import { tracker } from '@visikon/tracker/src';
import { MOBILE_SIZE, useWindowSizeMatches } from '@visikon/utils/src/responsive';
import * as React from 'react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { ContentConfig } from '../../common/ContentConfig';
import { useNavLinkHelper } from '../../common/NavigationHelper';
import { useContent } from '../../common/selectors';
import { ContentContainerSection } from '../../components/ContentContainerSection';
import { WithFooter } from '../../components/Footer';
import { FlexWrapper, SmallViewSidePadding } from '../../components/styled/ContentAreaContainers';
import { DetailsCardContainer } from '../../components/styled/DetailsCard';
import { ScreenHeader } from '../../components/styled/Texts';
import { I18N } from '../../internationalization/AppText';
import { FAQSection, FAQType } from '../../reducers/resourcesReducer';
import { useSelfServiceUnsavedChanges } from '../embeddedMessageScript/EmbeddedMessageScript';
import { FaqCardComponent } from './FaqCardComponent';
import * as FaqStyles from './FaqCardComponent.styles';

export const renderDetails = (info: FAQSection['questions'][0]) => {
  return (
    <FaqStyles.FaqDetails key={info.question}>
      <div style={{ alignItems: 'center', lineHeight: '1.5' }}>
        <span style={{ maxWidth: '100%' }}>{info.question}</span>
      </div>
      <div>{renderTextWithNewline(info.answer)}</div>
    </FaqStyles.FaqDetails>
  );
};

const renderTextWithNewline = (text: string) => {
  return text?.split('\n').map((t) => {
    if (t && t.length > 0) {
      return (
        <p key={t} style={{ lineHeight: '1.5' }}>
          {t}
        </p>
      );
    }
    return null;
  });
};

const preserveFaqProps = (apiContent: Translation<FAQType>, unsavedSymptoms: Translation<FAQType>) => {
  if (!apiContent || !unsavedSymptoms) {
    return null;
  }

  const findThumbnailData = (id: string) => {
    return apiContent.data?.sections?.find((section) => section.thumbnail?._id === id)?.thumbnail || null;
  };

  unsavedSymptoms?.data?.sections?.map((section: any) => {
    if (section.thumbnail && section.thumbnail._idRef) {
      section.thumbnail = findThumbnailData(section.thumbnail._idRef) || section.thumbnail;
    }
  });

  return unsavedSymptoms;
};

export const FaqComponent: React.FC<PropsWithChildren & { fieldKey: keyof ContentConfig }> = ({ fieldKey, children: header }) => {
  const content = useContent<FAQType>(fieldKey);
  const isMobileView = useWindowSizeMatches(MOBILE_SIZE);
  const unsavedChanges = useSelfServiceUnsavedChanges();
  const languageCode = backbone.store.getState().country.languageCode;

  const [currentIndexView, setCurrentIndexView] = useState<number | undefined>(undefined);
  const [detailView, setDetailView] = useState<FAQSection | undefined>(undefined);

  const getClickHandler = (faq: FAQSection, index: number) => {
    return () => {
      if (currentIndexView !== index) {
        tracker.trackScreen('FAQDetails');
        tracker.trackEvent('FAQ', 'faqShow', faq.name);
        setDetailView(faq);
        setCurrentIndexView(index);
      } else {
        tracker.trackScreen('FAQList');
        setDetailView(undefined);
        setCurrentIndexView(undefined);
      }
    };
  };

  const operation = unsavedChanges.operations.find((op) => op.contentType === fieldKey);
  const unsavedFaqs = preserveFaqProps(
    content,
    operation?.data.translations.find((t) => t.language === languageCode),
  );

  const sections =
    unsavedFaqs && !isMobileView
      ? unsavedFaqs.data?.sections ??
        (() => {
          throw new Error('There should be faq sections?');
        })()
      : content?.data?.sections || [];

  const faqSections = sections.map((section, i) => (
    <FaqCardComponent
      faq={section}
      currentPosition={i}
      key={i}
      isOpen={detailView ? i === currentIndexView : false}
      onClick={getClickHandler(section, i)}
      length={sections.length}
    />
  ));
  const sectionDetailView = detailView ? <DetailsCardContainer>{detailView.questions.map(renderDetails)}</DetailsCardContainer> : undefined;

  const headerText = isMobileView && detailView ? <ScreenHeader>{detailView.name}</ScreenHeader> : header;

  const { isMobileOrHasNoMenu, contentNavLink, translatedPage, updateNavLink } = useNavLinkHelper();

  const paths = LocationHooks.usePaths();
  const pathComingFrom = paths[paths.length - 1];
  const level = LocationHooks.useNavLevel();

  useEffect(() => {
    if (detailView && isMobileOrHasNoMenu) {
      updateNavLink({
        title: translatedPage,
        path: pathComingFrom,
        action: () => {
          setDetailView(undefined);
          updateNavLink(contentNavLink);
        },
      });
    }
  }, [isMobileView, detailView, level]);

  const contentView = isMobileView && sectionDetailView ? sectionDetailView : faqSections;
  return (
    <FlexWrapper>
      <ContentContainerSection style={{ display: 'block' }}>
        <SmallViewSidePadding>
          <WithFooter footerStyle={{ paddingTop: '16px' }}>
            {headerText}

            {content === undefined && (
              <div>
                <I18N text="loading_dotdotdot" />
                ...
              </div>
            )}

            {content && contentView}
          </WithFooter>
        </SmallViewSidePadding>
      </ContentContainerSection>
    </FlexWrapper>
  );
};
